/**
 * @description 媒体错误处理器 - 具有良好扩展性的设计
 * 支持视频、音频等多种媒体类型的错误处理
 * @author librajt
 */

/**
 * 媒体错误处理器类
 * 提供统一的媒体错误处理接口，支持多种媒体类型
 */
export class MediaErrorHandler {
    /**
     * 格式化错误事件信息，支持多种媒体类型
     * @param events 错误事件数组
     * @param mediaType 媒体类型 ('video' | 'audio' | 'media')
     * @returns 格式化后的错误信息
     */
    public formatErrorEvents(events: Event[], mediaType: string): Record<string, any> {
        if (!events || !Array.isArray(events) || events.length === 0) {
            return { message: 'No error events available' };
        }

        const formattedEvents = events.map((event, index) => {
            const eventInfo: Record<string, any> = {
                index,
                type: event.type || 'unknown',
                timestamp: event.timeStamp || Date.now(),
                mediaType,
                target: this.getEventTargetInfo(event.target, mediaType),
            };

            // 处理媒体错误
            const mediaError = this.extractMediaError(event.target, mediaType);
            if (mediaError) {
                eventInfo.mediaError = mediaError;
            }

            // 处理其他类型的错误事件
            const otherError = this.extractOtherError(event);
            if (otherError) {
                Object.assign(eventInfo, otherError);
            }

            return eventInfo;
        });

        return {
            count: events.length,
            mediaType,
            events: JSON.stringify(formattedEvents),
            summary: JSON.stringify(this.generateErrorSummary(formattedEvents, mediaType)),
        };
    }

    /**
     * 获取事件目标信息，支持多种媒体元素
     * @param target 事件目标
     * @param mediaType 媒体类型
     * @returns 目标信息
     */
    private getEventTargetInfo(target: EventTarget | null, mediaType: string): Record<string, any> {
        if (!target) {
            return { type: 'null' };
        }

        // 处理 HTMLVideoElement
        if (target instanceof HTMLVideoElement) {
            return this.getVideoElementInfo(target);
        }

        // 处理 HTMLAudioElement
        if (target instanceof HTMLAudioElement) {
            return this.getAudioElementInfo(target);
        }

        // 处理其他媒体元素（未来扩展）
        if (this.isMediaElement(target)) {
            return this.getGenericMediaElementInfo(target, mediaType);
        }

        return {
            type: target.constructor.name,
            stringified: String(target),
        };
    }

    /**
     * 获取视频元素信息
     * @param element HTMLVideoElement
     * @returns 视频元素信息
     */
    private getVideoElementInfo(element: HTMLVideoElement): Record<string, any> {
        return {
            type: 'HTMLVideoElement',
            tagName: element.tagName,
            src: element.src,
            currentSrc: element.currentSrc,
            readyState: element.readyState,
            networkState: element.networkState,
            videoWidth: element.videoWidth,
            videoHeight: element.videoHeight,
            error: element.error
                ? {
                      code: element.error.code,
                      message: element.error.message,
                  }
                : null,
        };
    }

    /**
     * 获取音频元素信息
     * @param element HTMLAudioElement
     * @returns 音频元素信息
     */
    private getAudioElementInfo(element: HTMLAudioElement): Record<string, any> {
        return {
            type: 'HTMLAudioElement',
            tagName: element.tagName,
            src: element.src,
            currentSrc: element.currentSrc,
            readyState: element.readyState,
            networkState: element.networkState,
            error: element.error
                ? {
                      code: element.error.code,
                      message: element.error.message,
                  }
                : null,
        };
    }

    /**
     * 获取通用媒体元素信息（为未来扩展预留）
     * @param element 媒体元素
     * @param mediaType 媒体类型
     * @returns 通用媒体元素信息
     */
    private getGenericMediaElementInfo(element: any, mediaType: string): Record<string, any> {
        return {
            type: element.constructor.name,
            mediaType,
            tagName: element.tagName,
            src: element.src || element.currentSrc,
            readyState: element.readyState,
            networkState: element.networkState,
            error: element.error
                ? {
                      code: element.error.code,
                      message: element.error.message,
                  }
                : null,
        };
    }

    /**
     * 判断是否为媒体元素
     * @param target 目标元素
     * @returns 是否为媒体元素
     */
    private isMediaElement(target: any): boolean {
        return (
            target &&
            (target instanceof HTMLVideoElement ||
                target instanceof HTMLAudioElement ||
                (target.readyState !== undefined && target.networkState !== undefined))
        );
    }

    /**
     * 提取媒体错误信息
     * @param target 事件目标
     * @param mediaType 媒体类型
     * @returns 媒体错误信息
     */
    private extractMediaError(target: EventTarget | null, mediaType: string): Record<string, any> | null {
        if (!target || !this.isMediaElement(target)) {
            return null;
        }

        const mediaElement = target as any;
        if (!mediaElement.error) {
            return null;
        }

        return {
            code: mediaElement.error.code,
            message: mediaElement.error.message,
            codeDescription: this.getMediaErrorDescription(mediaElement.error.code, mediaType),
        };
    }

    /**
     * 提取其他类型的错误信息
     * @param event 错误事件
     * @returns 其他错误信息
     */
    private extractOtherError(event: Event): Record<string, any> | null {
        // 处理 ErrorEvent
        if (event instanceof ErrorEvent) {
            return {
                errorEvent: {
                    message: event.message,
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                },
            };
        }

        // 处理 PromiseRejectionEvent
        if (event instanceof PromiseRejectionEvent) {
            return {
                promiseRejection: {
                    reason: event.reason,
                    promise: event.promise,
                },
            };
        }

        // 处理 CustomEvent
        if (event instanceof CustomEvent) {
            return {
                customEvent: {
                    detail: event.detail,
                },
            };
        }

        return null;
    }

    /**
     * 获取媒体错误代码的描述，支持多种媒体类型
     * @param code 错误代码
     * @param mediaType 媒体类型
     * @returns 错误描述
     */
    private getMediaErrorDescription(code: number, mediaType: string): string {
        const baseErrorDescriptions: Record<number, string> = {
            1: 'MEDIA_ERR_ABORTED - 用户中止了媒体下载',
            2: 'MEDIA_ERR_NETWORK - 网络错误导致媒体下载失败',
            3: 'MEDIA_ERR_DECODE - 媒体解码失败',
            4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - 媒体格式不被支持',
        };

        const mediaTypePrefix = mediaType === 'video' ? '视频' : mediaType === 'audio' ? '音频' : '媒体';

        const baseDescription = baseErrorDescriptions[code];
        if (baseDescription) {
            return `${baseDescription} (${mediaTypePrefix})`;
        }

        return `${mediaTypePrefix}未知错误代码: ${code}`;
    }

    /**
     * 生成错误摘要信息
     * @param formattedEvents 格式化后的事件数组
     * @param mediaType 媒体类型
     * @returns 错误摘要
     */
    private generateErrorSummary(formattedEvents: Record<string, any>[], mediaType: string): Record<string, any> {
        const summary: Record<string, any> = {
            totalErrors: formattedEvents.length,
            mediaType,
            errorTypes: {},
            hasMediaErrors: false,
            hasNetworkErrors: false,
            hasDecodeErrors: false,
            hasSourceErrors: false,
        };

        formattedEvents.forEach((event) => {
            // 统计错误类型
            const eventType = event.type || 'unknown';
            summary.errorTypes[eventType] = (summary.errorTypes[eventType] || 0) + 1;

            // 检查媒体错误类型
            if (event.mediaError) {
                summary.hasMediaErrors = true;
                summary.mediaErrorCode = event.mediaError.code;
                summary.mediaErrorMessage = event.mediaError.message;

                // 根据错误代码分类
                switch (event.mediaError.code) {
                    case 2:
                        summary.hasNetworkErrors = true;
                        break;
                    case 3:
                        summary.hasDecodeErrors = true;
                        break;
                    case 4:
                        summary.hasSourceErrors = true;
                        break;
                }
            }

            // 检查网络状态
            if (event.target?.networkState !== undefined) {
                summary.networkState = event.target.networkState;
            }
        });

        return summary;
    }
}

// 创建全局的媒体错误处理器实例
export const mediaErrorHandler = new MediaErrorHandler();
